import { formatDate } from '@angular/common';
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import {
  BodyScrollEndEvent,
  CellClassParams,
  ColDef,
  ColumnApi,
  GridApi,
  GridReadyEvent,
} from 'ag-grid-community';
import { ChartConfiguration } from 'chart.js';
import { ToastrService } from 'ngx-toastr';
import { DeviceMonitoringModalLinkComponent } from '../monitoring/device-monitoring-modal-link/device-monitoring-modal-link.component';
import {
  DeviceHealth,
  DeviceHealthQuery,
  DeviceHealthService,
} from './services/device.health.service';
import { StatusReportHeader } from './status-report-header/status-report-header.component';

@Component({
  selector: 'dcc-device-health',
  templateUrl: './device-health.component.html',
  styleUrls: ['./device-health.component.scss'],
})
export class DeviceHealthComponent implements OnInit {
  private readonly calibrationToolTip =
    'If calPickStatus is set to Calibrated then this is a pass, otherwise it is a fail.';
  private readonly checkInToolTip =
    'If there is Check In data for a device within 7 days, it is a pass. Otherwise, it is a fail.';
  private readonly ignitionToolTip =
    'If there is no ignition signal within 7 days of our latest GPS signal date, the device is marked as a pass.';
  private readonly gpsToolTip =
    'If there is Trackpoint data within 7 days of the most recent presence date, then the device is marked as a pass.';
  private readonly can1ConnectionToolTip =
    'The Connection Type of the Can1 port';
  private readonly ecmDataToolTip =
    'If there is ECM data present, we check the date of this data against our latest gps point. If the data within 7 days from the gps date, or the gps date is within 7 days of presence, the grade is a pass';
  private readonly sdCardHealthToolTip =
    'If there is an SDCard Success message, and that message has happened more recently than any SdCard failure message, the device is marked as a pass';
  private readonly hubAssociationToolTip =
    'If there is a follower present and the state is anything other than attached, the device is marked pass.';
  private readonly vehicleAssociationToolTip =
    'If there is a valid VehicleId and Vin associated with the device then the device is a pass.';
  private readonly vehicleMeasurementToolTip =
    'If there are valid vehicle measurements present in the associated fields: cameraHeight, cameraOffset, vehicleWidth then the device is a pass';
  private readonly accelerometerToolTip =
    'If there is accelerometer data present, and the time stamp is less than 7 days old from the last reported gps date, then it is healthy';
  private readonly presenceToolTip =
    'If there is either a presence On or Presence Off within the last 7 days of the most recent gps date, this grade will be a pass ';
  private readonly inServiceToolTip =
    'If the device is reporting as InService, then the device is marked as a pass';
  private readonly installEcmIgnitionToolTip =
    'If the device had an ECM Ignition signal during install';
  private readonly installEcmEngineSpeedToolTip =
    'If the device had an ECM Engine Speed signal during install';

  statusFilter: string;
  serialNumberFilter: [];
  healthParamFilter: [];
  companyIdFilter: number = null;
  tooltipShowDelay = 0;
  isLoading = false;

  get percentageHealthy(): number {
    if (!this.totalDevices) {
      return 100;
    }

    return Math.ceil((this.healthyCount / this.totalDevices) * 100);
  }

  get percentageUnhealthy(): number {
    return 100 - this.percentageHealthy;
  }

  healthyCount = 0;
  totalDevices = 0;
  unhealthyCount = 0;
  unhealthyDevices = 0;
  serialNumbers = '';
  healthReportType = 'Device';
  sortOrder = 'desc';
  sortColumn: string = null;
  pageCount = 1;
  pageNumber = 1;
  paginationPageSize = 100;
  paginationPageMaxSize = 500;
  gridReady = false;
  isHealthSearched = false;

  unhealthyParamTotals: { [key: string]: number } = {
    checkInGrade: 0,
    presenceGrade: 0,
    ignitionGrade: 0,
    accelerometerGrade: 0,
    calibrationGrade: 0,
    sdCardHealthGrade: 0,
    gpsGrade: 0,
    inServiceGrade: 0,
    ecmDataGrade: 0,
    hubAssociationGrade: 0,
    vehicleAssociationGrade: 0,
    vehicleMeasurementGrade: 0,
    installEcmIgnitionGrade: 0,
    installEcmEngineSpeedGrade: 0,
    totalGrade: 0,
  };

  counts = [];

  private gridApi!: GridApi<DeviceHealth>;
  private gridColumnApi!: ColumnApi;

  healthQuery: DeviceHealthQuery = {
    selectedQuery: '',
    serialNumbers: [],
    healthParams: [],
    companyId: null,
    sortOrder: '',
    startDate: null,
    endDate: null,
    scrollId: null,
    size: 20,
  };
  scrollId: string = this.healthQuery.scrollId;
  currentQuery: DeviceHealthQuery;
  loadingCsv = false;
  columnDefs: ColDef[] = [];
  columnDefsMain: ColDef[] = [
    {
      field: 'serialNumber',
      headerName: 'DEVICE',
      headerComponent: StatusReportHeader,
      headerComponentParams: {
        activeStatusCriteria: false,
        sortable: false,
        noSort: true,
      },
      suppressSizeToFit: true,
      cellRenderer: DeviceMonitoringModalLinkComponent,
    },
    {
      field: 'companyName',
      headerName: 'COMPANY',
      headerComponent: StatusReportHeader,
      headerComponentParams: {
        activeStatusCriteria: false,
        sortable: false,
        noSort: true,
      },
      suppressSizeToFit: true,
    },
    {
      field: 'companyId',
      headerName: 'ID',
      headerComponent: StatusReportHeader,
      headerComponentParams: {
        activeStatusCriteria: false,
        sortable: false,
        noSort: true,
      },
      suppressSizeToFit: true,
    },
    {
      field: 'groupName',
      headerName: 'GROUP',
      headerComponent: StatusReportHeader,
      headerComponentParams: {
        activeStatusCriteria: false,
        sortable: false,
        noSort: true,
      },
      suppressSizeToFit: true,
    },
    {
      field: 'can1Type',
      headerName: 'CAN1',
      headerComponent: StatusReportHeader,
      headerComponentParams: {
        activeStatusCriteria: false,
        sortable: false,
        noSort: true,
      },
      headerTooltip: this.can1ConnectionToolTip,
      suppressSizeToFit: true,
      cellStyle: this.cellStyle,
    },
    {
      field: 'installerType',
      headerName: 'INSTALL',
      headerComponent: StatusReportHeader,
      headerComponentParams: {
        activeStatusCriteria: false,
        sortable: false,
        noSort: true,
      },
      suppressSizeToFit: true,
    },
    {
      field: 'firmwareVersion',
      headerName: 'FIRMWARE VERSION AT INSTALL',
      headerComponent: StatusReportHeader,
      headerComponentParams: {
        activeStatusCriteria: false,
        sortable: false,
        noSort: true,
      },
      suppressSizeToFit: true,
    },
    {
      field: 'installDate',
      headerName: 'INSTALL COMPLETE',
      headerComponent: StatusReportHeader,
      headerComponentParams: {
        activeStatusCriteria: false,
        sortable: false,
        noSort: true,
      },
      width: 185,
      valueFormatter: this.datetimeFormatter,
      suppressSizeToFit: true,
    },
    {
      headerName: 'DAYS SINCE COMPLETION',
      headerComponent: StatusReportHeader,
      headerComponentParams: {
        activeStatusCriteria: false,
        sortable: false,
        noSort: true,
      },
      valueGetter: this.calculateDaysSinceCompletion,
      suppressSizeToFit: true,
    },
    {
      field: 'totalGrade',
      headerName: `STATUS`,
      headerComponent: StatusReportHeader,
      headerComponentParams: {
        activeStatusCriteria: false,
        sortable: false,
        noSort: true,
      },
      suppressSizeToFit: true,
      cellStyle: this.cellStyleTotal,
    },
    {
      field: 'checkInGrade',
      headerName: `CHECK-IN`,
      headerComponent: StatusReportHeader,
      headerComponentParams: {
        activeStatusCriteria: false,
        sortable: false,
        noSort: true,
      },
      headerTooltip: this.checkInToolTip,
      suppressSizeToFit: true,
      cellStyle: this.cellStyle,
      cellRenderer: this.cellRender,
    },
    {
      field: 'presenceGrade',
      headerName: `ACTIVE`,
      headerComponent: StatusReportHeader,
      headerComponentParams: {
        activeStatusCriteria: false,
        sortable: false,
        noSort: true,
      },
      headerTooltip: this.presenceToolTip,
      suppressSizeToFit: true,
      cellStyle: this.cellStyle,
      cellRenderer: this.cellRender,
    },
    {
      field: 'inServiceGrade',
      headerName: `IN-SERVICE`,
      headerComponent: StatusReportHeader,
      headerComponentParams: {
        activeStatusCriteria: false,
        sortable: false,
        noSort: true,
      },
      headerTooltip: this.inServiceToolTip,
      suppressSizeToFit: true,
      cellStyle: this.cellStyle,
      cellRenderer: this.cellRender,
    },
    {
      field: 'ignitionGrade',
      headerName: `IGNITION`,
      headerComponent: StatusReportHeader,
      headerComponentParams: {
        activeStatusCriteria: false,
        sortable: false,
        noSort: true,
      },
      headerTooltip: this.ignitionToolTip,
      suppressSizeToFit: true,
      cellStyle: this.cellStyle,
      cellRenderer: this.cellRender,
    },
    {
      field: 'gpsGrade',
      headerName: `GPS`,
      headerComponent: StatusReportHeader,
      headerComponentParams: {
        activeStatusCriteria: false,
        sortable: false,
        noSort: true,
      },
      headerTooltip: this.gpsToolTip,
      suppressSizeToFit: true,
      cellStyle: this.cellStyle,
      cellRenderer: this.cellRender,
    },
    {
      field: 'sdCardHealthGrade',
      headerName: `SD CARD`,
      headerComponent: StatusReportHeader,
      headerComponentParams: {
        activeStatusCriteria: false,
        sortable: false,
        noSort: true,
      },
      headerTooltip: this.sdCardHealthToolTip,
      suppressSizeToFit: true,
      cellStyle: this.cellStyle,
      cellRenderer: this.cellRender,
    },
    {
      field: 'calibrationGrade',
      headerName: `CALIBRATION`,
      headerComponent: StatusReportHeader,
      headerComponentParams: {
        activeStatusCriteria: false,
        sortable: false,
        noSort: true,
      },
      headerTooltip: this.calibrationToolTip,
      suppressSizeToFit: true,
      cellStyle: this.cellStyle,
      cellRenderer: this.cellRender,
    },
    {
      field: 'hubAssociationGrade',
      headerName: `HUB`,
      headerComponent: StatusReportHeader,
      headerComponentParams: {
        activeStatusCriteria: false,
        sortable: false,
        noSort: true,
      },
      headerTooltip: this.hubAssociationToolTip,
      suppressSizeToFit: true,
      cellStyle: this.cellStyle,
      cellRenderer: this.cellRender,
    },
    {
      field: 'vehicleAssociationGrade',
      headerName: `VEHICLE`,
      headerComponent: StatusReportHeader,
      headerComponentParams: {
        activeStatusCriteria: false,
        sortable: false,
        noSort: true,
      },
      headerTooltip: this.vehicleAssociationToolTip,
      suppressSizeToFit: true,
      cellStyle: this.cellStyle,
      cellRenderer: this.cellRender,
    },
    {
      field: 'vehicleMeasurementGrade',
      headerName: `MEASURE.`,
      headerComponent: StatusReportHeader,
      headerComponentParams: {
        activeStatusCriteria: false,
        sortable: false,
        noSort: true,
      },
      headerTooltip: this.vehicleMeasurementToolTip,
      suppressSizeToFit: true,
      cellStyle: this.cellStyle,
      cellRenderer: this.cellRender,
    },
    {
      field: 'accelerometerGrade',
      headerName: `ACCEL.`,
      headerComponent: StatusReportHeader,
      headerComponentParams: {
        activeStatusCriteria: false,
        sortable: false,
        noSort: true,
      },
      headerTooltip: this.accelerometerToolTip,
      suppressSizeToFit: true,
      cellStyle: this.cellStyle,
      cellRenderer: this.cellRender,
    },
    {
      field: 'ecmDataGrade',
      headerName: `ECM DATA`,
      headerComponent: StatusReportHeader,
      headerComponentParams: {
        activeStatusCriteria: false,
        sortable: false,
        noSort: true,
      },
      headerTooltip: this.ecmDataToolTip,
      suppressSizeToFit: true,
      cellStyle: this.cellStyle,
      cellRenderer: this.cellRender,
    },
    {
      field: 'installEcmIgnitionGrade',
      headerName: `INSTALL ECM IGNITION`,
      headerComponent: StatusReportHeader,
      headerComponentParams: {
        activeStatusCriteria: false,
        sortable: false,
        noSort: true,
      },
      headerTooltip: this.installEcmIgnitionToolTip,
      suppressSizeToFit: true,
      cellStyle: this.cellStyle,
      cellRenderer: this.cellRender,
    },
    {
      field: 'installEcmEngineSpeedGrade',
      headerName: `INSTALL ECM ENGINE SPEED`,
      headerComponent: StatusReportHeader,
      headerComponentParams: {
        activeStatusCriteria: false,
        sortable: false,
        noSort: true,
      },
      headerTooltip: this.installEcmEngineSpeedToolTip,
      suppressSizeToFit: true,
      cellStyle: this.cellStyle,
      cellRenderer: this.cellRender,
    },
  ];

  @ViewChild('countContainerRef', { static: false }) countContainer: ElementRef;
  defaultColumnSortOrder: string[] = [
    'DEVICE',
    'COMPANY',
    'ID',
    'GROUP',
    'CAN1',
    'INSTALL COMPLETE',
    'FIRMWARE VERSION AT INSTALL',
    'INSTALL',
    'DAYS SINCE COMPLETION',
    'STATUS',
  ];

  healthChartLabels: string[] = ['', ''];

  healthChartDatasets: ChartConfiguration<'bar'>['data']['datasets'] = [
    { data: [this.healthyCount], label: 'Pass', backgroundColor: '#D5DCE6' },
    {
      data: [this.totalDevices - this.healthyCount],
      label: 'Fail',
      backgroundColor: '#F99898',
    },
  ];

  healthChartOptions: ChartConfiguration<'bar'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    datasets: {
      bar: {
        maxBarThickness: 16,
      },
    },
    elements: {
      bar: {
        borderSkipped: 'middle',
        borderRadius: 50,
      },
    },
    plugins: {
      tooltip: {
        xAlign: 'left',
        yAlign: 'center',
        position: 'nearest',
        displayColors: false,
      },
    },
    scales: {
      x: {
        stacked: true,
        display: false,
      },
      y: {
        stacked: true,
        display: false,
      },
    },
  };

  private updateChartData() {
    this.healthChartDatasets = [
      { data: [this.healthyCount], label: 'Pass', backgroundColor: '#D5DCE6' },
      {
        data: [this.totalDevices - this.healthyCount],
        label: 'Fail',
        backgroundColor: '#F99898',
      },
    ];
  }

  defaultColDef: ColDef = {
    resizable: true,
    initialWidth: 130,
    wrapHeaderText: true,
    autoHeaderHeight: true,
  };

  constructor(
    private deviceHealthService: DeviceHealthService,
    private toastr: ToastrService,
  ) {}

  ngOnInit(): void {
    this.columnDefs = this.columnDefsMain;
  }

  private autoSizeSpecificColumns() {
    const columnIds: string[] = ['companyName', 'serialNumber', 'groupName'];
    this.gridColumnApi.autoSizeColumns(columnIds, false);
  }

  private calculateDaysSinceCompletion(params) {
    const dateTime = params.data.timestamp;
    const timeDiff = new Date().getTime() - new Date(dateTime).getTime();
    const daysPassed = Math.floor(timeDiff / (1000 * 3600 * 24));
    return daysPassed;
  }

  private datetimeFormatter(params) {
    return new Date(params.value).toLocaleString();
  }

  private cellRender(params: CellClassParams) {
    if (params.value === '*fail') {
      return 'fail';
    }
    return params.value;
  }

  private cellStyle(params: CellClassParams) {
    if (params.value === '*fail') {
      return {
        color: '#FF0000',
      };
    } else {
      return {
        color: '#1B2431',
      };
    }
  }

  private cellStyleTotal(params: CellClassParams) {
    if (params.value === 'fail') {
      return {
        color: '#FF0000',
        fontWeight: 'bold',
        backgroundColor: '#EAEDF2',
      };
    }
    return {
      fontWeight: 'bold',
      backgroundColor: '#EAEDF2',
    };
  }

  private updateColumnDefs(
    columns: string[],
    selectedHealthParams: string[],
  ): void {
    this.columnDefs = this.columnDefsMain.filter((colDef) =>
      columns.includes(colDef.field),
    );

    const mappedSelectedHealthParams = selectedHealthParams.map((healthParam) =>
      this.mapHealthParam(healthParam),
    );

    const columnOrders = [
      ...this.defaultColumnSortOrder,
      ...mappedSelectedHealthParams,
    ];

    this.columnDefs = this.mapOrder(
      this.columnDefs,
      columnOrders,
      'headerName',
    );

    this.columnDefs.forEach((colDef) => {
      mappedSelectedHealthParams.includes(colDef.headerName)
        ? (colDef.headerComponentParams.activeStatusCriteria = true)
        : (colDef.headerComponentParams.activeStatusCriteria = false);
    });
  }

  private mapHealthParam(healthParam: string): string {
    switch (healthParam) {
      case 'Check In':
        return 'CHECK-IN';
      case 'Calibration':
        return 'CALIBRATION';
      case 'Presence':
        return 'ACTIVE';
      case 'Ignition':
        return 'IGNITION';
      case 'Gps':
        return 'GPS';
      case 'Accelerometer':
        return 'ACCEL.';
      case 'Sd Card Health':
        return 'SD CARD';
      case 'In Service':
        return 'IN-SERVICE';
      case 'Ecm Data':
        return 'ECM DATA';
      case 'Hub Association':
        return 'HUB';
      case 'Vehicle Association':
        return 'VEHICLE';
      case 'Vehicle Measurement':
        return 'MEASURE.';
      case 'Install Ecm Ignition':
        return 'INSTALL ECM IGNITION';
      case 'Install Ecm Engine Speed':
        return 'INSTALL ECM ENGINE SPEED';
      default:
        return healthParam;
    }
  }

  private mapOrder(unsorted: any[], sortOrder: any[], key: string) {
    const order = sortOrder.reduce((r, k, i) => ((r[k] = i + 1), r), {});
    return unsorted.sort(
      (a, b) => (order[a[key]] || Infinity) - (order[b[key]] || Infinity),
    );
  }

  private async getData(query: DeviceHealthQuery): Promise<void> {
    query.size = this.paginationPageSize;
    try {
      const response =
        await this.deviceHealthService.getDeviceHealthResponse(query);

      if (response.total > 0) {
        this.healthyCount = response.totalHealthy;
        this.totalDevices = response.total;
        this.isHealthSearched = true;
        this.unhealthyParamTotals = response.unhealthyCount;
        this.unhealthyCount = response.totalUnhealthy;
        const columnsOfInterest = this.findColumnsOfInterest(response.devices);
        this.gridApi.showLoadingOverlay();
        this.updateColumnDefs(columnsOfInterest, query.healthParams);
        this.pageCount = Math.ceil(this.totalDevices / this.paginationPageSize);
        const flaggedData = this.filterData(query, response.devices);
        this.gridApi.setRowData(flaggedData);
        this.gridApi.sizeColumnsToFit();
        this.gridApi.refreshToolPanel();
        this.gridApi.refreshCells({ force: true });
        this.gridApi.hideOverlay();
        if (this.currentQuery.selectedQuery === 'Install') {
          this.gridApi.ensureColumnVisible('installDate');
        }
        this.updateChartData();
        this.updateCounts();
        if (response.total < this.paginationPageSize) {
          this.scrollId = null;
          this.healthQuery.scrollId = null;
        } else {
          this.scrollId = response.scrollId;
          this.healthQuery.scrollId = this.scrollId;
        }
      } else {
        this.gridApi.setRowData([]);
        this.toastr.info('No results were fetched');
        this.updateChartData();
      }
    } catch (error) {
      console.log(error);
      this.gridApi.setRowData([]);
      this.totalDevices = 0;
      this.isLoading = false;
      this.toastr.error('Failure retrieving data.');
      this.updateChartData();
    }
  }

  private async scrollData(query: DeviceHealthQuery): Promise<void> {
    try {
      this.gridApi.showLoadingOverlay();
      const response =
        await this.deviceHealthService.getDeviceHealthResponse(query);
      if (response.devices.length > 0) {
        const flaggedData = this.filterData(query, response.devices);
        this.gridApi.setRowData(flaggedData);
        this.gridApi.hideOverlay();
        this.scrollId = response.scrollId;
        this.healthQuery.scrollId = this.scrollId;
      } else {
        this.gridApi.setRowData([]);
        this.toastr.info('End of Results. Please Run another query');
      }
    } catch (error) {
      this.gridApi.setRowData([]);
      this.totalDevices = 0;
      this.toastr.error('Failure retrieving data.');
    }
  }

  private filterData(query: DeviceHealthQuery, data: DeviceHealth[]) {
    data.forEach((d) => {
      query.healthParams.forEach((param) => {
        if (d[healthParamToFieldMap[param]] === 'fail') {
          d[healthParamToFieldMap[param]] = '*fail';
        }
      });
    });
    return data;
  }

  private hideOrShowInstallSpecificColumns(query: DeviceHealthQuery) {
    if (query.selectedQuery === 'Device') {
      this.columnDefs = this.columnDefs.map((column) => {
        if (
          column.headerName === 'INSTALL COMPLETE' ||
          column.headerName === 'FIRMWARE VERSION AT INSTALL' ||
          column.headerName === 'DAYS SINCE COMPLETION'
        ) {
          return {
            ...column,
            hide: true,
          };
        }
        return column;
      });
    } else if (query.selectedQuery === 'Install') {
      this.columnDefs = this.columnDefs.map((column) => {
        if (
          column.headerName === 'INSTALL COMPLETE' ||
          column.headerName === 'FIRMWARE VERSION AT INSTALL' ||
          column.headerName === 'DAYS SINCE COMPLETION'
        ) {
          return {
            ...column,
            hide: false,
          };
        }
        return column;
      });
    }
  }

  async queryData(query: DeviceHealthQuery) {
    this.isLoading = true;
    if (query.scrollId !== null && query.scrollId !== undefined) {
      await this.deviceHealthService.deleteScrollId(this.currentQuery);
      query.scrollId = null;
      this.currentQuery.scrollId = null;
      this.healthQuery.scrollId = null;
    }
    this.resetPagination();
    this.healthQuery.scrollId = null;
    this.healthyCount = 0;
    this.currentQuery = query;
    this.hideOrShowInstallSpecificColumns(query);
    this.healthQuery = query;
    this.gridApi?.showLoadingOverlay();
    await this.getData(query);
    this.isLoading = false;
    this.gridApi?.hideOverlay();
    this.gridApi?.sizeColumnsToFit();
  }

  private findColumnsOfInterest(devices: DeviceHealth[]): string[] {
    const nonEmptyColumns = [];
    for (const column of Object.keys(devices[0])) {
      for (const device of devices) {
        if (device[column]) {
          nonEmptyColumns.push(column);
          break;
        }
      }
    }
    nonEmptyColumns.push('groupName'); // needed until all records have groupName
    return nonEmptyColumns;
  }

  private updateCounts() {
    this.counts = [];
    const columns = this.gridColumnApi.getAllDisplayedColumns();
    columns.forEach((column) => {
      if (this.shouldDisplayCountForColumn(column.getColId())) {
        const left = column.getLeft();
        const width = column.getActualWidth();
        let value;
        if (column.getColId() === 'installerType') {
          value = 'Total Fails:';
        } else if (column.getColId() === 'totalGrade') {
          value = this.unhealthyCount;
        } else {
          value = this.unhealthyParamTotals[column.getColId()];
        }
        this.counts.push({ left: `${left}px`, width: `${width}px`, value });
      }
    });
  }

  private shouldDisplayCountForColumn(colId: string): boolean {
    let colsToDisplay = [];
    colsToDisplay = this.gridColumnApi.getAllDisplayedColumns().slice(-14);
    return colsToDisplay.includes(this.gridColumnApi.getColumn(colId));
  }

  async onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;

    await this.queryData(this.healthQuery);
    this.autoSizeSpecificColumns();
    window.addEventListener('resize', () => {
      setTimeout(() => {
        params.api.sizeColumnsToFit();
      });
    });
    this.updateCounts();
    this.gridApi.addEventListener('columnResized', () => this.updateCounts());
    this.gridApi.addEventListener('columnMoved', () => this.updateCounts());
    this.gridApi.addEventListener('columnVisible', () => this.updateCounts());
    this.gridApi.addEventListener('displayedColumnsChanged', () =>
      this.updateCounts(),
    );
    this.gridApi.addEventListener('bodyScroll', this.syncScroll.bind(this));
  }

  syncScroll(_: BodyScrollEndEvent) {
    const scrollLeft = this.gridApi.getHorizontalPixelRange().left;
    const scrollRight = this.gridApi.getHorizontalPixelRange().right;
    this.countContainer.nativeElement.scrollLeft = scrollLeft;
    this.countContainer.nativeElement.scrollRight = scrollRight;
  }

  async onClickFirstPage(_: any) {
    this.resetPagination();
    await this.getData(this.healthQuery);
  }

  async onClickNext(event: any) {
    this.pageNumber++;
    await this.scrollData(this.healthQuery);
  }

  private resetPagination() {
    this.pageNumber = 1;
    this.healthQuery.scrollId = null;
  }

  async downloadCsv(): Promise<void> {
    this.loadingCsv = true;
    const response = await this.deviceHealthService.getDeviceHealthResponseCsv(
      this.currentQuery,
    );
    const tempElemement = document.createElement('a');
    tempElemement.href = 'data:text/csv,' + encodeURIComponent(response);
    const filename = `${formatDate(new Date(), 'short', 'en-US')}_${this.currentQuery.selectedQuery}Health_Lytx.csv`;
    tempElemement.setAttribute('download', filename);
    document.body.appendChild(tempElemement);
    tempElemement.click();
    document.body.removeChild(tempElemement);

    this.loadingCsv = false;
  }
}

const healthParamToFieldMap: { [key: string]: string } = {
  Calibration: 'calibrationGrade',
  'Check In': 'checkInGrade',
  Presence: 'presenceGrade',
  Ignition: 'ignitionGrade',
  Accelerometer: 'accelerometerGrade',
  'Sd Card Health': 'sdCardHealthGrade',
  Gps: 'gpsGrade',
  'In Service': 'inServiceGrade',
  'Vehicle Association': 'vehicleAssociationGrade',
  'Vehicle Measurement': 'vehicleMeasurementGrade',
  'Ecm Data': 'ecmDataGrade',
  'Hub Association': 'hubAssociationGrade',
  'Install Ecm Ignition': 'installEcmIgnitionGrade',
  'Install Ecm Engine Speed': 'installEcmEngineSpeedGrade',
};
